import dayjs, {Dayjs} from 'dayjs';
import {useSelector} from 'react-redux';
import businessDays from 'dayjs-business-days2';
import {DateRange} from '@mui/x-date-pickers-pro';
import {useQueryClient} from '@tanstack/react-query';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {LinearProgress} from '@mui/material';
import {apiPost} from '../../axiosConfig';
import {
  StepperLayout,
  useStepperState,
} from '../../components/layouts/stepper/StepperLayout';
import {ISponsorshipRequestProp} from '../../dto/Sponsor.dto';
import {useStoreSelector} from '../../store/configstore';
import {companyIdSelector, isGTSelector} from '../../store/Slices/companySlice';
import SponsorshipInformation from './SponsorshipInformation';
import SponsorshipRequest from './SponsorshipRequest';
import {IMultipleFileUploadProp} from './MultipleFileUploadCmpt';
import SponsorshipRequestReview from './SponsorshipRequestReview';
import DialogApiRequestStatus from '../../components/dialogs/DialogApiRequestStatus';
import AboutSponsor from './SponsorDetails';
import {
  queryKeySponsorList,
  useRqGetSponsorById,
} from '../../react_query/sponsor_commercial/SponsorCommercialQuery';
import {trimInBetweenNames} from '../../Service/nameValidation';

dayjs.extend(businessDays);

export const multipleFileUploadValidation = (
  multipleFileUpload: IMultipleFileUploadProp[],
) => {
  return (
    multipleFileUpload.length > 0 &&
    multipleFileUpload.every(val => val.file && val.fileName)
  );
};

export const duplicateFileNameExist = (
  multipleFileUpload: IMultipleFileUploadProp[],
) => {
  return multipleFileUpload
    .filter(v => v.fileName)
    .some((val, _index, arr) =>
      arr.some(v => v.id !== val.id && v.fileName === val.fileName),
    );
};

export const nameIncrementing = (
  dropdownOption: number,
  sponsorName: string,
) => {
  const values: IMultipleFileUploadProp[] = Array.from(
    {length: dropdownOption},
    (_, i) => ({
      id: uuidv4(),
      fileName: `${sponsorName}${i ? '_' + i : ''}`,
    }),
  );
  return values;
};

const SponsorRequest = ({
  setIsOpenCreateModal,
  updateId,
}: {
  setIsOpenCreateModal: React.Dispatch<React.SetStateAction<string>>;
  updateId: number | null;
}) => {
  const isGT = useSelector(isGTSelector);
  const queryClient = useQueryClient();
  const {activeStepIndex, onNextStep, onPreviousStep, onStepClick} =
    useStepperState(updateId ? 1 : 0);
  const [apiState, setApiState] = useState({status: '', error: null});
  const companyId = useStoreSelector(companyIdSelector);
  const [sponsorDetails, setSponsorDetails] = useState({
    sponsor_name: '',
    main_contact: '',
    email_address: '',
    phone_number: '',
  });
  const [multipleFileUpload, setMultipleFileUpload] = useState<
    IMultipleFileUploadProp[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [stepperLoadedOn, setStepperLoadedOn] = useState<Dayjs | null>(null);
  useEffect(() => {
    setStepperLoadedOn(dayjs());
  }, []);

  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(() => {
    const initialStartDate: Dayjs = dayjs();

    let endDate: Dayjs;
    if (isGT) {
      const defaultEndDate = dayjs().set('month', 5).set('date', 30);
      endDate = initialStartDate.isAfter(defaultEndDate)
        ? defaultEndDate.add(1, 'year')
        : defaultEndDate;
    } else {
      endDate = dayjs().add(1, 'year');
    }

    return [initialStartDate, endDate];
  });

  const [adSpot, setAdspot] = useState(1);
  const [isContactSponsor, setIsContactSponsor] = useState(updateId ? 2 : -1);
  const [sponsorshipRequest, setSponsorshipRequest] =
    useState<ISponsorshipRequestProp>({
      request_format: '',
      files: undefined,
      message: '',
    });
  const [sponsorDetailsValidate, setSponsorDetailsValidate] = useState(false);

  const {data: sponsorFull, isFetching} = useRqGetSponsorById(
    companyId,
    Number(updateId),
  );

  const isDuplicateFileName = useMemo(
    () => duplicateFileNameExist(multipleFileUpload),
    [multipleFileUpload],
  );

  const steps = useMemo(() => {
    const isDateRangeFilled = !dateRange.some(value => value === null);
    const sponsorshipRequestNotEmpty =
      sponsorshipRequest.files !== undefined ||
      sponsorshipRequest.track !== undefined ||
      sponsorshipRequest.message !== '' ||
      (multipleFileUploadValidation(multipleFileUpload) &&
        !isDuplicateFileName);

    const stepList = [
      {
        label: 'About Sponsor',
        disabled: false,
      },
      {
        label: 'Enter Sponsor details',
        disabled: false,
      },
      {
        label: 'Review',
        disabled:
          isContactSponsor === 1 || isContactSponsor === 2
            ? !sponsorDetailsValidate || !isDateRangeFilled
            : !sponsorshipRequestNotEmpty,
      },
    ];
    if (isContactSponsor === 0) {
      stepList.splice(2, 0, {
        label: 'Sponsorship Info',
        disabled: !sponsorDetailsValidate || !isDateRangeFilled,
      });
    }
    return stepList;
  }, [
    dateRange,
    sponsorshipRequest.files,
    sponsorshipRequest.track,
    sponsorshipRequest.message,
    multipleFileUpload,
    isDuplicateFileName,
    isContactSponsor,
    sponsorDetailsValidate,
  ]);

  const handleSubmit = () => {
    setApiState({
      status: 'pending',
      error: null,
    });
    const payload = new FormData();
    payload.append(
      `sponsor_details[sponsor_name]`,
      trimInBetweenNames(sponsorDetails['sponsor_name']),
    );
    payload.append(
      `sponsor_details[main_contact]`,
      trimInBetweenNames(sponsorDetails['main_contact']),
    );
    payload.append(
      `sponsor_details[email_address]`,
      sponsorDetails['email_address'],
    );
    payload.append(
      `sponsor_details[phone_number]`,
      sponsorDetails['phone_number'],
    );
    payload.append(`start_date`, dateRange[0]?.format('YYYY/MM/DD') || '');
    payload.append(`end_date`, dateRange[1]?.format('YYYY/MM/DD') || '');
    payload.append(`adspot`, adSpot.toString());

    if (isContactSponsor !== 2) {
      // Not for reactivate sponsor request
      payload.append(`is_contact_sponsor`, isContactSponsor.toString());
      if (isContactSponsor === 0) {
        payload.append(
          `sponsorship_info[request_format]`,
          sponsorshipRequest['request_format'],
        );
        if (sponsorshipRequest.files) {
          payload.append(
            `sponsorship_info[files]`,
            sponsorshipRequest['files'] as Blob,
          );
        }
        if (sponsorshipRequest.track) {
          payload.append(
            `sponsorship_info[track]`,
            String(sponsorshipRequest.track.id),
          );
          // Track is a uploaded processed file (By comparing the created time of track, declare as "New Processed file" or "Choose from exist tracks")
          if (
            sponsorshipRequest?.track?.created_at &&
            stepperLoadedOn?.isBefore(
              dayjs(sponsorshipRequest?.track?.created_at),
            )
          ) {
            // The track_name of media_track only got updated with Sponsor name
            payload.append(`sponsorship_info[track_from]`, 'processed_file');
          } else {
            // The selected track duplicate with the track_name as Sponsor name
            payload.append(`sponsorship_info[track_from]`, 'exist_track');
          }
        }
        payload.append(
          `sponsorship_info[message]`,
          sponsorshipRequest['message'] as string,
        );

        if (multipleFileUpload) {
          multipleFileUpload.forEach((val, key) => {
            payload.append(
              `sponsorship_info[multiple_files][${key}][file_name]`,
              val.fileName,
            );
            payload.append(
              `sponsorship_info[multiple_files][${key}][file]`,
              val.file as Blob,
            );
          });
        }
      }
    }

    apiPost(
      updateId && isContactSponsor === 2
        ? `/v1/company/${companyId}/sponsor/${updateId}/update?_method=PUT`
        : `/v1/company/${companyId}/sponsor`,
      payload,
    )
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });

        queryClient.invalidateQueries({
          queryKey: [queryKeySponsorList(companyId), 'active'],
          refetchType: 'active',
        });
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };
  const handleCancel = () => {
    setIsOpenCreateModal('');
  };
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);

  useEffect(() => {
    if (selectedOption && setMultipleFileUpload) {
      setSponsorshipRequest({
        request_format: '2',
        files: undefined,
        message: '',
      });
      setMultipleFileUpload(
        nameIncrementing(selectedOption, sponsorDetails.sponsor_name),
      );
    } else {
      setMultipleFileUpload([]);
    }
  }, [selectedOption, setMultipleFileUpload, sponsorDetails.sponsor_name]);

  useEffect(() => {
    if (!isFetching && updateId && sponsorFull) {
      setSponsorDetails({
        sponsor_name: sponsorFull.sponsor.sponsor_name,
        main_contact: sponsorFull.sponsor.main_contact,
        email_address: sponsorFull.sponsor.email,
        phone_number: sponsorFull.sponsor.phone_number,
      });
      setAdspot(sponsorFull.ad_spots);
      setSponsorDetailsValidate(true);
    }
  }, [isFetching, sponsorFull, updateId]);

  return (
    <StepperLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      onBack={onPreviousStep}
      onNext={onNextStep}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onStepClick={onStepClick}
      isSubmitted={apiState.status !== ''}
      setClose={handleCancel}
      title='Add Sponsor Details'>
      {(() => {
        const label = steps?.[activeStepIndex]?.label ?? '';
        switch (label) {
          case 'About Sponsor':
            return <AboutSponsor />;
          case 'Enter Sponsor details':
            return (
              <>
                {isFetching && <LinearProgress />}
                <SponsorshipInformation
                  sponsorDetails={sponsorDetails}
                  setSponsorDetails={setSponsorDetails}
                  setSponsorDetailsValidate={setSponsorDetailsValidate}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  isContactSponsor={isContactSponsor}
                  onContactSponsorChange={setIsContactSponsor}
                  setAdspot={setAdspot}
                  adSpot={adSpot}
                  updateId={updateId}
                />
              </>
            );
          case 'Sponsorship Info':
            return (
              <SponsorshipRequest
                sponsorshipRequest={sponsorshipRequest}
                setSponsorshipRequest={setSponsorshipRequest}
                type={0}
                multipleFileUpload={multipleFileUpload}
                setMultipleFileUpload={setMultipleFileUpload}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                isDuplicateFileName={isDuplicateFileName}
              />
            );
          case 'Review':
            return (
              <SponsorshipRequestReview
                sponsorDetails={sponsorDetails}
                dateRange={dateRange}
                isContactSponsor={isContactSponsor}
                adSpot={adSpot}
                sponsorshipRequest={sponsorshipRequest}
                multipleFileUpload={multipleFileUpload}
                updateSponsor={sponsorFull}
              />
            );
          default:
            return null;
        }
      })()}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={handleCancel}
        />
      )}
    </StepperLayout>
  );
};
export default SponsorRequest;
